import PropTypes from 'prop-types';
import { useEffect } from 'react';

import CouponsList from 'components/Coupons/CouponsList';
import ErrorBoundary from 'components/ErrorBoundary';
import GenericError from 'components/GenericError';
import FlatLayout from 'components/Layout/FlatLayout';

import placeholder from 'lib/placeholder';
import { trackEvent } from 'lib/posthog';
import { couponQueryKeys } from 'lib/queryKeys';
import { sendSupixEvent } from 'lib/supix';

import { useUtm } from 'providers/UtmProvider';

import { getCouponsList } from 'services/coupon';

import Breadcrumb from './Breadcrumb';
import CouponsHead from './Head';
import HighlightedCouponsStores from './HighlightedCouponsStores';
import CouponsPageDescription from './PageDescription';
import PopularCouponsStores from './PopularCouponsStores';
import CouponsSchema from './Schema';
import StoreWithCouponsSearchBox from './StoreWithCouponsSearchBox';

import COUPONS from 'constants/coupons';

const GA_EVENT_CATEGORY = 'home_coupons';

const Coupons = ({
  serverCoupons,
  featuredStores = [],
  popularStores = [],
}) => {
  const { isUtmLoaded } = useUtm();

  useEffect(() => {
    if (isUtmLoaded) {
      sendSupixEvent('/cp', { id: 0 });
    }
  }, [isUtmLoaded]);

  useEffect(() => {
    trackEvent('view coupon index page', {
      type: 'screen',
      source_type: 'navigation',
    });
  }, []);

  return (
    <>
      <FlatLayout.Margin>
        <FlatLayout.Heading>
          <CouponsHead
            activeCoupons={serverCoupons?.coupons}
            featuredStores={featuredStores}
          />
          <CouponsSchema />
          <Breadcrumb />
        </FlatLayout.Heading>
        <FlatLayout.Body className="gap-10 lg:gap-12">
          <ErrorBoundary>
            <PopularCouponsStores
              gaEventCategory={GA_EVENT_CATEGORY}
              stores={popularStores}
            />
          </ErrorBoundary>
          <StoreWithCouponsSearchBox gaEventCategory={GA_EVENT_CATEGORY} />
          <ErrorBoundary
            fallback={
              <GenericError
                message={placeholder('errors.couldNotLoadCouponsList')}
              />
            }
          >
            <CouponsList
              gaEventCategory={GA_EVENT_CATEGORY}
              initialCoupons={serverCoupons}
              queryFn={({ page }, opts) =>
                getCouponsList(
                  { limit: COUPONS.COUPONS_LIMIT, after: page, sort: 'best' },
                  opts
                )
              }
              queryKey={couponQueryKeys.list({
                sortType: 'best',
              })}
              withCouponStoreCover
            />
          </ErrorBoundary>
          {featuredStores && (
            <ErrorBoundary>
              <HighlightedCouponsStores stores={featuredStores} />
            </ErrorBoundary>
          )}
          <CouponsPageDescription />
        </FlatLayout.Body>
      </FlatLayout.Margin>
      <FlatLayout.Footer />
    </>
  );
};

Coupons.propTypes = {
  featuredStores: PropTypes.arrayOf(
    PropTypes.shape({
      storeImage: PropTypes.string.isRequired,
      storeSlug: PropTypes.string.isRequired,
      storeName: PropTypes.string.isRequired,
    })
  ),
  serverCoupons: PropTypes.shape({
    after: PropTypes.string,
    coupons: PropTypes.arrayOf(
      PropTypes.shape({
        couponId: PropTypes.number.isRequired,
        couponDiscountValue: PropTypes.string.isRequired,
        couponDiscountShort: PropTypes.string.isRequired,
        couponDiscount: PropTypes.string.isRequired,
        couponDiscountOn: PropTypes.string.isRequired,
        couponStatusName: PropTypes.string.isRequired,
        couponInstructions: PropTypes.string.isRequired,
        couponVerified: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
        storeImage: PropTypes.string,
        storeName: PropTypes.string.isRequired,
        storeDomain: PropTypes.string.isRequired,
        storeSlug: PropTypes.string.isRequired,
        couponTag: PropTypes.arrayOf(PropTypes.string.isRequired),
        couponPublished: PropTypes.string,
        couponTitle: PropTypes.string.isRequired,
        couponCode: PropTypes.string.isRequired,
        couponUrl: PropTypes.string.isRequired,
        userName: PropTypes.string,
      })
    ).isRequired,
  }).isRequired,
  popularStores: PropTypes.arrayOf(
    PropTypes.shape({
      storeName: PropTypes.string,
      storeImage: PropTypes.string,
      storeSlug: PropTypes.string,
      totalCoupons: PropTypes.number,
    })
  ),
};

export default Coupons;
